import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import api, { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { getDocs, collection, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { motion, AnimatePresence } from 'framer-motion';

const ITEMS_PER_PAGE = 10;

const EMPTY = {
  name: '',
  nameSindhi: ''
};

export default function ManageLiteraryFields() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    if (DB_TYPE === 'firebase') {
      try {
        const q = query(collection(db, 'literary-fields'), orderBy('name'));
        const snap = await getDocs(q);
        setItems(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    } else {
      api.get('/literary-fields').then(r => {
        setItems(r.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async e => {
    e.preventDefault();
    try {
      if (DB_TYPE === 'firebase') {
        if (editId) {
          await updateDoc(doc(db, 'literary-fields', editId), form);
          toast.success('Updated');
        } else {
          await addDoc(collection(db, 'literary-fields'), { ...form, createdAt: new Date().toISOString() });
          toast.success('Field added');
        }
      } else {
        if (editId) { await api.put(`/literary-fields/${editId}`, form); }
        else { await api.post('/literary-fields', form); }
        toast.success('Success');
      }
      setModal(false);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Error');
    }
  };

  const del = async id => {
    if (!window.confirm('Delete this field?')) return;
    try {
      if (DB_TYPE === 'firebase') { await deleteDoc(doc(db, 'literary-fields', id)); }
      else { await api.delete(`/literary-fields/${id}`); }
      toast.success('Deleted');
      load();
    } catch { toast.error('Error'); }
  };

  const isRTL = t.language === 'sindhi';

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {t('manageLiteraryFields')}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {t('literaryFieldsDescription')}
          </p>
        </div>
        <motion.button 
          whileHover={{ scale: 1.05 }} 
          whileTap={{ scale: 0.95 }} 
          className="btn btn-primary" 
          style={{ height: 48, padding: '0 24px', borderRadius: 12, fontSize: 14, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} 
          onClick={() => { 
            setForm(EMPTY); 
            setEditId(null); 
            setModal(true); 
          }}
        >
          {t('addField')}
        </motion.button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <AnimatePresence mode="popLayout">
            {items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 80, background: 'white', borderRadius: 24, border: '1px solid var(--sindhi-cream)', direction: isRTL ? 'rtl' : 'ltr' }}>{t('noResults')}</div>
            ) : (
              <Card style={{ padding: 0, overflow: 'hidden' }}>
                <table className="table" style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <thead>
                    <tr style={{ background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)' }}>
                      <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('fieldName')}</th>
                      <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('fieldNameSindhi')}</th>
                      <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(item => (
                      <tr key={item._id} style={{ borderBottom: '1px solid var(--sindhi-cream)' }}>
                        <td style={{ padding: '16px 24px', fontSize: 14 }}>{item.name}</td>
                        <td style={{ padding: '16px 24px', fontSize: 20, fontFamily: 'var(--font-sindhi)', color: 'var(--sindhi-maroon)' }}>{item.nameSindhi}</td>
                        <td style={{ padding: '16px 24px' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn-action" style={{ background: 'var(--sindhi-ivory)', color: 'var(--sindhi-navy)', border: '1px solid var(--sindhi-cream)', padding: '6px 10px', borderRadius: 8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setForm(item); setEditId(item._id); setModal(true); }}>{t('edit')}</button>
                            <button className="btn-action" style={{ background: '#feecef', color: '#e11d48', border: 'none', padding: '6px 10px', borderRadius: 8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => del(item._id)}>{t('delete')}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            ) }
          </AnimatePresence>
          <Pagination page={page} totalPages={Math.ceil(items.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,25,47,0.4)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} onClick={e => e.target === e.currentTarget && setModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="modal-box" style={{ width: 500, borderRadius: 24, background: 'white', padding: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
              <div style={{ padding: '24px 32px', background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 22, fontWeight: 800, color: 'var(--sindhi-navy)' }}>{editId ? t('edit') : t('submit')} {t('literaryField')}</h2>
                <button style={{ background: 'white', border: '1px solid var(--sindhi-cream)', width: 36, height: 36, borderRadius: 10, cursor: 'pointer' }} onClick={() => setModal(false)}>✕</button>
              </div>
              <form onSubmit={save} style={{ padding: 32 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <div className="form-group">
                    <label className="form-label">{t('fieldName')} *</label>
                    <input className="form-input" placeholder="e.g., Poetry" value={form.name} onChange={e => set('name', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontFamily: 'var(--font-sindhi)'}}>{t('fieldNameSindhi')} *</label>
                    <input className="form-input" placeholder="مثال طور: شاعري" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.nameSindhi} onChange={e => set('nameSindhi', e.target.value)} required />
                  </div>
                </div>

                <div style={{ marginTop: 32, display: 'flex', gap: 12, borderTop: '1px solid var(--sindhi-cream)', paddingTop: 24 }}>
                  <button type="submit" className="btn btn-primary" style={{ height: 50, flex: 1, borderRadius: 12, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('save')}</button>
                  <button type="button" onClick={() => setModal(false)} className="btn btn-outline" style={{ height: 50, padding: '0 32px', borderRadius: 12, fontWeight: 800, border: '1px solid var(--sindhi-cream)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('cancel')}</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
